import { useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatCents, formatDate } from '../lib/format.js';

const { t } = i18n;

interface Batch {
  id: string;
  periodFrom: string;
  periodTo: string;
  totalCents: number;
  currency: string;
  status: 'DRAFT' | 'PUSHED' | 'FAILED';
  invoiceNumber: string | null;
  invoiceUrl: string | null;
  attempts: number;
  lastError: string | null;
  pushedAt: string | null;
  createdAt: string;
}

const STATUS_CLASS: Record<Batch['status'], string> = {
  DRAFT: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100',
  PUSHED: 'bg-green-100 text-green-900 border-green-300 dark:bg-green-900/40 dark:text-green-100',
  FAILED: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-900/40 dark:text-red-100',
};

export function BillingBatchesTable({
  customerId,
  reloadKey,
}: {
  customerId: string;
  reloadKey: number;
}) {
  const [batches, setBatches] = useState<Batch[] | null>(null);

  useEffect(() => {
    setBatches(null);
    api<Batch[]>(`/customers/${customerId}/billing`)
      .then(setBatches)
      .catch(() => setBatches([]));
  }, [customerId, reloadKey]);

  if (batches === null) return null;
  if (batches.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('billing.list.title')}
        </h3>
        <div className="text-sm text-muted-foreground">{t('billing.list.empty')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {t('billing.list.title')}
      </h3>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left">
            <tr>
              <th className="px-3 py-2 font-medium">{t('billing.col.created')}</th>
              <th className="px-3 py-2 font-medium">{t('billing.col.period')}</th>
              <th className="px-3 py-2 font-medium">{t('billing.col.total')}</th>
              <th className="px-3 py-2 font-medium">{t('billing.col.status')}</th>
              <th className="px-3 py-2 font-medium">{t('billing.col.invoice')}</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id} className="border-t border-border align-top">
                <td className="px-3 py-2 text-xs">{formatDate(b.createdAt)}</td>
                <td className="px-3 py-2 text-xs">
                  {formatDate(b.periodFrom)} → {formatDate(b.periodTo)}
                </td>
                <td className="px-3 py-2 tabular-nums">
                  {formatCents(b.totalCents, b.currency)}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[b.status]}`}
                  >
                    {t(`billing.status.${b.status}`)}
                    {b.status === 'DRAFT' && b.attempts > 0 ? ` · ${b.attempts}` : ''}
                  </span>
                  {b.lastError ? (
                    <div className="mt-1 max-w-xs truncate text-xs text-muted-foreground" title={b.lastError}>
                      {b.lastError}
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-xs">
                  {b.invoiceUrl ? (
                    <a
                      href={b.invoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      {b.invoiceNumber ?? t('billing.col.open')}
                    </a>
                  ) : b.invoiceNumber ? (
                    <span className="font-mono">{b.invoiceNumber}</span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
